import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { toast } from 'sonner';
import { ArrowRight, ScanLine, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { DiscrepancyDialog } from '@/components/fc/DiscrepancyDialog';
import { CloseSessionDialog } from '@/components/fc/CloseSessionDialog';
import { MANIFEST_TYPE_LABELS, MANIFEST_STATUS_LABELS } from '@/types/manifest.types';
import { DISCREPANCY_REASON_LABELS, DiscrepancyReason } from '@/types/trip.types';
import { ReceivingState, SessionTripView } from '@/types/receiving.types';

export default function ReceivingSession() {
  const { nodeId, manifestId } = useParams<{ nodeId: string; manifestId: string }>();
  const navigate = useNavigate();
  const { get, post } = useApi();

  const [state, setState] = useState<ReceivingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanInput, setScanInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [discrepancyTrip, setDiscrepancyTrip] = useState<SessionTripView | null>(null);
  const [discrepancySubmitting, setDiscrepancySubmitting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  const loadState = useCallback(async () => {
    if (!manifestId || !nodeId) return;
    try {
      const res: any = await get(`/api/v1/dashboard/manifests/${manifestId}/receiving/state?node_id=${nodeId}`);
      setState(res?.data ?? res);
    } catch {
      toast.error('فشل في جلب حالة الجلسة');
    } finally {
      setLoading(false);
    }
  }, [manifestId, nodeId, get]);

  useEffect(() => { loadState(); }, [loadState]);

  const handleScan = async () => {
    const numbers = scanInput
      .split(/\r?\n|,|\s+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (numbers.length === 0) return;
    setScanning(true);
    try {
      const res: any = await post(
        `/api/v1/dashboard/manifests/${manifestId}/receiving/scan?node_id=${nodeId}`,
        { tracking_numbers: numbers },
      );
      const data = res?.data ?? res;
      const success: string[] = data?.success ?? [];
      const failed: { tracking_number: string; reason: string }[] = data?.failed ?? [];
      if (success.length > 0) toast.success(`تم استلام ${success.length} شحنة`);
      if (failed.length > 0) {
        toast.error(`${failed.length} فشلت — ${failed[0].reason}`);
      }
      setScanInput('');
      await loadState();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'فشل المسح');
    } finally {
      setScanning(false);
    }
  };

  const handleDiscrepancy = async (reason: DiscrepancyReason, notes: string) => {
    if (!discrepancyTrip) return;
    setDiscrepancySubmitting(true);
    try {
      await post(`/api/v1/dashboard/manifests/${manifestId}/receiving/discrepancy`, {
        trip_id: discrepancyTrip.id,
        reason,
        notes: notes || undefined,
      });
      toast.success('تم تسجيل التباين');
      setDiscrepancyTrip(null);
      await loadState();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'فشل تسجيل التباين');
    } finally {
      setDiscrepancySubmitting(false);
    }
  };

  const handleClose = async (driverAcknowledged: boolean, driverName: string) => {
    setClosing(true);
    try {
      await post(
        `/api/v1/dashboard/manifests/${manifestId}/receiving/close?node_id=${nodeId}`,
        { driver_acknowledged: driverAcknowledged, driver_acknowledged_name: driverName },
      );
      toast.success('تم إغلاق الجلسة');
      navigate(`/dashboard/fc/${nodeId}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'فشل إغلاق الجلسة');
    } finally {
      setClosing(false);
      setCloseOpen(false);
    }
  };

  const failedCount = useMemo(
    () => (state?.trips ?? []).filter(t => t.status === 'failed').length,
    [state],
  );
  const scannedCleanCount = (state?.scanned_count ?? 0) - failedCount;

  if (loading) {
    return (
      <div className="p-6"><LoadingSkeleton type="details" /></div>
    );
  }

  if (!state) {
    return (
      <div className="p-6 text-center" dir="rtl">
        <p>لم يتم العثور على الجلسة</p>
        <Button className="mt-4" onClick={() => navigate(`/dashboard/fc/${nodeId}`)}>عودة</Button>
      </div>
    );
  }

  const { manifest, trips, scanned_count, unscanned_count, can_close } = state;

  return (
    <div className="p-4 md:p-6 space-y-4" dir="rtl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/dashboard/fc/${nodeId}`)}>
          <ArrowRight className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl md:text-2xl font-bold">
              جلسة استلام: {MANIFEST_TYPE_LABELS[manifest.manifest_type]}
            </h1>
            <span className="font-mono text-sm text-muted-foreground">({manifest.manifest_number})</span>
            <Badge variant="secondary">{MANIFEST_STATUS_LABELS[manifest.status]}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            السائق: {(manifest as any).driver?.user?.name || (manifest as any).driver?.name || 'غير معين'}
          </p>
        </div>
        <Button
          variant="default"
          disabled={!can_close}
          onClick={() => setCloseOpen(true)}
        >
          إغلاق الجلسة
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-4 text-center">
          <p className="text-xs text-muted-foreground">المجموع</p>
          <p className="text-2xl font-bold">{trips.length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4 text-center">
          <p className="text-xs text-muted-foreground">تم الاستلام</p>
          <p className="text-2xl font-bold text-green-700">{scannedCleanCount}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4 text-center">
          <p className="text-xs text-muted-foreground">تباينات</p>
          <p className="text-2xl font-bold text-red-700">{failedCount}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4 text-center">
          <p className="text-xs text-muted-foreground">متبقي</p>
          <p className="text-2xl font-bold text-orange-600">{unscanned_count}</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Scan input */}
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base flex items-center gap-2">
            <ScanLine className="h-5 w-5" /> مسح الشحنات
          </CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              rows={8}
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder="ضع أرقام التتبع هنا (واحد لكل سطر)..."
              dir="ltr"
              className="font-mono text-sm"
              disabled={scanning}
            />
            <Button
              className="w-full"
              onClick={handleScan}
              disabled={scanning || !scanInput.trim() || scanned_count === trips.length}
            >
              {scanning ? 'جارٍ المسح...' : 'مسح المحدد'}
            </Button>
            <p className="text-xs text-muted-foreground">
              يمكنك لصق عدة أرقام دفعة واحدة (سطر لكل رقم).
            </p>
          </CardContent>
        </Card>

        {/* Trip list */}
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">الشحنات المتوقعة ({trips.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {trips.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between border rounded p-2"
                >
                  <div className="flex items-center gap-2">
                    {t.status === 'completed' ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : t.status === 'failed' ? (
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                    ) : (
                      <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
                    )}
                    <span className="font-mono text-sm">{t.tracking_number}</span>
                    {t.recipient_city && (
                      <span className="text-xs text-muted-foreground">→ {t.recipient_city}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {t.status === 'completed' && <Badge variant="default" className="bg-green-600">تم الاستلام</Badge>}
                    {t.status === 'failed' && (
                      <Badge variant="destructive">
                        {t.discrepancy_reason ? DISCREPANCY_REASON_LABELS[t.discrepancy_reason] : 'تباين'}
                      </Badge>
                    )}
                    {t.status !== 'completed' && t.status !== 'failed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDiscrepancyTrip(t)}
                      >
                        تسجيل تباين
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <DiscrepancyDialog
        open={!!discrepancyTrip}
        onOpenChange={(o) => { if (!o) setDiscrepancyTrip(null); }}
        trackingNumber={discrepancyTrip?.tracking_number ?? ''}
        submitting={discrepancySubmitting}
        onSubmit={handleDiscrepancy}
      />

      <CloseSessionDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        scannedCount={scannedCleanCount}
        failedCount={failedCount}
        totalCount={trips.length}
        submitting={closing}
        mode="receiving"
        onSubmit={handleClose}
      />
    </div>
  );
}
